import type { MenuItem, Order } from "@/types";

/**
 * The "Order Again" ranking — the effect inside the original's `nq`
 * (`recovery-source/live-chunks/mod-9802.js`), extracted so it can be unit
 * tested. `nq` itself is `src/components/menu/OrderAgain.tsx`.
 */

/**
 * One remembered line. The catalog is the authority for price, image and
 * availability; the ORDER is the authority for name, quantity and the chosen
 * options, because those are what the customer actually bought.
 */
export interface ReorderableItem {
  _id: string;
  item_id: string;
  name: string;
  price?: number;
  discounted_price?: number | null;
  discount_amount?: number;
  is_discount_percentage?: boolean;
  image_url?: string;
  sold_out?: boolean;
  show_starting_from?: boolean;
  quantity: number;
  /** Option names, in the order they were chosen. */
  modifiers: string[];
  orderFrequency: number;
  isFavorite: boolean;
}

/**
 * Verbatim rules from `nq`'s effect: iterate the orders in the order the
 * `history` slice holds them; skip any line whose `item_id` is not in the live
 * catalog; on a repeat, increment `orderFrequency` and set
 * `isFavorite = orderFrequency > 3`; on a first sighting, snapshot the
 * catalog's price fields and the ORDER's name, quantity and options. Then sort
 * by frequency descending, breaking ties on `name.localeCompare`, and take the
 * first 30.
 *
 * Note `orderFrequency` counts ORDERS the item appears in, not units bought —
 * a single order for five of something scores 1.
 *
 * ⚠ Shape difference, not a behaviour change: the original walks
 * `order.categories[].items[]` and runs the chosen options through
 * `getReceiptItemModifiers` to build indented receipt lines. `/web/orders/*`
 * already flattens categories away and returns the options as a plain list, so
 * the same options arrive here as names.
 */
export function buildReorderableItems(
  orders: Order[],
  catalogItemsMap: Record<string, MenuItem>,
): ReorderableItem[] {
  const byItemId = new Map<string, ReorderableItem>();
  for (const order of orders) {
    for (const line of order.items ?? []) {
      const catalogItem = catalogItemsMap[line.itemId];
      if (!catalogItem) continue;
      const existing = byItemId.get(line.itemId);
      if (existing) {
        existing.orderFrequency += 1;
        existing.isFavorite = existing.orderFrequency > 3;
        continue;
      }
      byItemId.set(line.itemId, {
        _id: line.itemId,
        item_id: line.itemId,
        name: line.name,
        price: catalogItem.price,
        discounted_price: catalogItem.discounted_price,
        discount_amount: catalogItem.discount_amount,
        is_discount_percentage: catalogItem.is_discount_percentage,
        image_url: catalogItem.image_url,
        sold_out: catalogItem.sold_out,
        show_starting_from: catalogItem.show_starting_from,
        quantity: line.quantity,
        modifiers: (line.modifiers ?? []).map((modifier) => modifier.name),
        orderFrequency: 1,
        isFavorite: false,
      });
    }
  }
  return Array.from(byItemId.values())
    .sort((a, b) =>
      b.orderFrequency !== a.orderFrequency
        ? b.orderFrequency - a.orderFrequency
        : a.name.localeCompare(b.name),
    )
    .slice(0, 30);
}
